import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { i18nLocale, weekdayLabel, timeAgo } from "../lib/locale";
import {
  CheckCircle2, Clock, Lock, Sparkles, CalendarDays,
  Users, Bot, Mail, RefreshCw, RefreshCcw, ChevronDown, User,
} from "lucide-react";

const STATUS_STYLE = {
  pending:   { bg: "#FEF3C7", fg: "#92400E" },
  confirmed: { bg: "#E8F0EA", fg: "#1A4D2E" },
  accepted:  { bg: "#E8F0EA", fg: "#1A4D2E" },
  refused:   { bg: "#FDE4E2", fg: "#A53A2F" },
  completed: { bg: "#E8E5FF", fg: "#3F2EB2" },
  cancelled: { bg: "#FDE4E2", fg: "#A53A2F" },
};

const PROPOSAL_STATUS_STYLE = {
  notified:  { bg: "#FEF3C7", fg: "#92400E" },
  seen:      { bg: "#E0ECFF", fg: "#1D4ED8" },
  accepted:  { bg: "#E8F0EA", fg: "#1A4D2E" },
  confirmed: { bg: "#E8F0EA", fg: "#1A4D2E" },
  declined:  { bg: "#FDE4E2", fg: "#A53A2F" },
  refused:   { bg: "#FDE4E2", fg: "#A53A2F" },
  expired:   { bg: "#F0F2EF", fg: "#6B726A" },
};

const fmtDate = (iso) => {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(i18nLocale(), { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
};
const fmtWeekday = (iso) => {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(i18nLocale(), { weekday: "short" });
  } catch { return ""; }
};


function Avatar({ name, url, size = 9 }) {
  const initials = (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  if (url) return <img src={url} alt={name} className="rounded-full object-cover shrink-0" style={{ width: size * 4, height: size * 4 }} />;
  return (
    <div className="rounded-full bg-[#E8F0EA] text-[#1A4D2E] grid place-items-center text-xs font-semibold shrink-0"
      style={{ width: size * 4, height: size * 4 }}>{initials}</div>
  );
}

function StatusChip({ status }) {
  const { t } = useTranslation();
  const style = STATUS_STYLE[status] || { bg: "#F0F2EF", fg: "#6B726A" };
  return (
    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full whitespace-nowrap"
      style={{ background: style.bg, color: style.fg }}>{t(`status.${status}`, status)}</span>
  );
}

/* ================= Remplaçants tab ================= */
export function CandidatesTab({ need, bookings, matchData, matchLoading, proposals = [], onChanged, onNavigateFull }) {
  const { t } = useTranslation();

  const matchById = useMemo(() => {
    const m = {};
    (matchData?.results || []).forEach((r) => { m[String(r.id).replace(/^locked-/, "")] = r; });
    return m;
  }, [matchData]);
  const notified = proposals.slice(0, 12);
  const activeBookings = bookings.filter((b) => b.status !== "cancelled");

  return (
    <div className="space-y-6" data-testid="need-tab-content-candidates">
      <div>
        <div className="text-xs uppercase tracking-wider font-semibold text-[#6B726A] mb-3 flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5" /> {t("cand_tab.on_need", { count: activeBookings.length })}
        </div>
        {activeBookings.length === 0 ? (
          <div className="text-sm text-[#6B726A] p-4 rounded-xl bg-[#F7F8F6]">{t("cand_tab.none")}</div>
        ) : (
          <div className="space-y-2">
            {activeBookings.map((b) => (
              <div key={b.id} className="flex items-center gap-3 p-3 rounded-xl border border-[#E2E5E0]" data-testid={`need-booking-${b.id}`}>
                <Avatar name={b.replacement_name} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{b.replacement_name || t("needs_page.role_fallback")}</div>
                  <div className="text-xs text-[#6B726A]">
                    {(b.start_date || b.need_snapshot?.start_date) ? `${fmtDate(b.start_date || b.need_snapshot?.start_date)}` : ""}
                    {(b.end_date || b.need_snapshot?.end_date) && (b.end_date || b.need_snapshot?.end_date) !== (b.start_date || b.need_snapshot?.start_date)
                      ? ` → ${fmtDate(b.end_date || b.need_snapshot?.end_date)}` : ""}
                  </div>
                </div>
                <StatusChip status={b.status} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="text-xs uppercase tracking-wider font-semibold text-[#6B726A] mb-3 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> {t("cand_tab.notified")}
        </div>
        {matchLoading ? (
          <div className="text-sm text-[#6B726A] p-4">{t("cand_tab.searching")}</div>
        ) : notified.length === 0 ? (
          <div className="text-sm text-[#6B726A] p-4 rounded-xl bg-[#F7F8F6]">{t("cand_tab.no_notified")}</div>
        ) : (
          <div className="space-y-2">
            {notified.map((p) => {
              const m = matchById[p.candidate_id];
              const locked = m?.locked;
              const name = locked ? m.name : (p.candidate_name || m?.name || t("needs_page.role_fallback"));
              const avatarUrl = p.candidate_avatar
                ? (p.candidate_avatar.startsWith("http") ? p.candidate_avatar : `${process.env.REACT_APP_BACKEND_URL}${p.candidate_avatar}`)
                : null;
              const pStyle = PROPOSAL_STATUS_STYLE[p.status] || { bg: "#F0F2EF", fg: "#6B726A" };
              return (
                <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl border border-[#E2E5E0] hover:border-[#1A4D2E]/30 transition-colors"
                  data-testid={`need-notified-${p.candidate_id}`}>
                  {locked ? (
                    <div className="w-9 h-9 rounded-full bg-[#F0F2EF] grid place-items-center shrink-0"><Lock className="w-4 h-4 text-[#6B726A]" /></div>
                  ) : (
                    <Avatar name={name} url={avatarUrl} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{name}</div>
                    <div className="text-xs text-[#6B726A] truncate">
                      {(locked ? "" : p.candidate_role) || m?.role_type || t("needs_page.role_fallback")}
                      {typeof m?.distance_km === "number" ? ` · ${Math.round(m.distance_km)} km` : ""}
                      {!locked && (p.candidate_skills || []).length > 0 ? ` · ${(p.candidate_skills || []).slice(0, 3).join(", ")}` : ""}
                    </div>
                  </div>
                  {!locked && p.candidate_permanent_available && need?.mode === "permanent" && (
                    <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-[#E0F2F1] text-[#0E6E5D] border border-[#7FCBC4] whitespace-nowrap inline-flex items-center gap-1"
                      data-testid={`perm-badge-${p.candidate_id}`}>
                      <RefreshCcw className="w-2.5 h-2.5" /> Recrutement permanent
                    </span>
                  )}
                  {typeof p.score === "number" && p.score > 0 && (
                    <span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#E8F0EA] text-[#1A4D2E] whitespace-nowrap">
                      {p.score}%
                    </span>
                  )}
                  <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full whitespace-nowrap"
                    style={{ background: pStyle.bg, color: pStyle.fg }}
                    data-testid={`need-notified-status-${p.candidate_id}`}>
                    {t(`cand_tab.prop_${p.status}`, p.status)}
                  </span>
                  {locked ? (
                    <button onClick={onNavigateFull} className="text-xs px-3 py-1.5 rounded-full border border-[#E2E5E0] text-[#6B726A] hover:bg-[#F7F8F6]"
                      data-testid={`need-suggestion-unlock-${p.candidate_id}`}>{t("cand_tab.unlock")}</button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= Créneaux tab ================= */
const fmtDateTime = (iso) => {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString(i18nLocale(), { day: "2-digit", month: "2-digit" })} ${d.toLocaleTimeString(i18nLocale(), { hour: "2-digit", minute: "2-digit" })}`;
  } catch { return ""; }
};

export function RecruitmentCandidatesTab({ recruitment }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const load = () => api.get(`/recruitments/${recruitment.id}/candidates`)
    .then(({ data }) => setRows(data))
    .catch(() => setRows([]));

  useEffect(() => { setRows(null); load(); }, [recruitment.id]); // eslint-disable-line

  const decide = async (p, accept) => {
    setConfirming(`${p.id}-${accept ? "a" : "r"}`);
    try {
      await api.post(`/recruitments/${recruitment.id}/proposals/${p.id}/decision`, { accept });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setConfirming(null);
    }
  };

  const matchLabel = (v) =>
    v >= 85 ? t("slots.match_excellent") : v >= 75 ? t("slots.match_verygood") : v >= 60 ? t("slots.match_good") : t("slots.match_possible");

  return (
    <div className="space-y-5" data-testid="recruit-tab-candidates">
      <div className="rounded-2xl border border-[#BFDBFE]">
        <div className="w-full flex items-center gap-2.5 px-4 py-3 rounded-t-2xl bg-[#EFF6FF]">
          <RefreshCcw className="w-4 h-4 text-[#0E6E5D] shrink-0" />
          <span className="text-sm font-semibold text-[#1E3A8A]">{t("needs_page.tab_permanent_candidates", "Candidats permanents")}</span>
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white text-[#1E3A8A]" data-testid="recruit-candidates-count">{rows ? rows.length : "…"}</span>
        </div>
        {rows === null ? (
          <div className="px-4 py-4 text-xs text-[#6B726A] border-t border-[#E2E5E0]">{t("cand_tab.searching")}</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-4 text-xs text-[#6B726A] border-t border-[#E2E5E0]">{t("slots.no_candidates_slot")}</div>
        ) : (
          <div className="border-t border-[#E2E5E0]">
            <div className="hidden md:grid px-4 py-2 gap-3 text-[10px] uppercase tracking-wider font-semibold text-[#6B726A] bg-[#FAFBF9] border-b border-[#E2E5E0] md:grid-cols-[minmax(180px,1.3fr)_minmax(110px,0.8fr)_minmax(90px,0.7fr)_minmax(180px,1.1fr)_auto]">
              <div>{t("slots.col_candidate")}</div>
              <div>{t("slots.col_compat")}</div>
              <div>{t("slots.col_distance", "Distance")}</div>
              <div>{t("slots.col_acceptance")}</div>
              <div className="text-right">{t("slots.col_actions", "Actions")}</div>
            </div>
            <div className="divide-y divide-[#F0F2EF]">
              {rows.map((p) => {
                const avatarUrl = p.candidate_avatar
                  ? (p.candidate_avatar.startsWith("http") ? p.candidate_avatar : `${process.env.REACT_APP_BACKEND_URL}${p.candidate_avatar}`)
                  : null;
                const score = typeof p.score === "number" ? p.score : null;
                const accepted = p.status === "accepted";
                const confirmedByYou = p.status === "confirmed";
                const declined = p.status === "declined";
                const refused = p.status === "refused";
                return (
                  <div key={p.candidate_id}
                    className="px-4 py-3.5 grid gap-3 items-center md:grid-cols-[minmax(180px,1.3fr)_minmax(110px,0.8fr)_minmax(90px,0.7fr)_minmax(180px,1.1fr)_auto]"
                    data-testid={`recruit-candidate-${p.candidate_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar name={p.candidate_name} url={avatarUrl} />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{p.candidate_name}</div>
                        <div className="text-xs text-[#6B726A] truncate">{p.candidate_role || t("needs_page.role_fallback")}</div>
                        <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#E0F2F1] text-[#0E6E5D] border border-[#7FCBC4]"
                          data-testid={`perm-badge-recruit-${p.candidate_id}`}>
                          <RefreshCcw className="w-2.5 h-2.5" /> Recrutement permanent
                        </span>
                        {(p.candidate_weekdays || []).length > 0 && (
                          <div className="mt-1 flex items-center gap-1 flex-wrap" data-testid={`cand-days-${p.candidate_id}`}>
                            <CalendarDays className="w-3 h-3 text-[#0E6E5D] shrink-0" />
                            {p.candidate_weekdays.map((d) => (
                              <span key={d} className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-[#F0FAF8] text-[#0E6E5D] border border-[#7FCBC4]/60">
                                {weekdayLabel(d, "short")}
                              </span>
                            ))}
                          </div>
                        )}
                        {p.candidate_experience_years > 0 && (
                          <div className="text-[11px] text-[#6B726A] flex items-center gap-1 mt-0.5">
                            <Users className="w-3 h-3" /> {t("slots.exp_years", { count: p.candidate_experience_years })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      {score !== null ? (
                        <>
                          <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: "#E8F0EA", color: score >= 75 ? "#1A9E55" : "#C97A20" }}>{score}%</span>
                          <div className="h-1.5 w-24 bg-[#E2E5E0] rounded-full mt-1.5 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${score}%`, background: score >= 75 ? "#1A9E55" : "#C97A20" }} />
                          </div>
                          <div className="text-[11px] text-[#4B5563] mt-1">{matchLabel(score)}</div>
                        </>
                      ) : <span className="text-xs text-[#6B726A]">—</span>}
                    </div>
                    <div className="text-sm">
                      {typeof p.candidate_distance_km === "number" ? (
                        <>
                          <div className="font-medium">{p.candidate_distance_km.toLocaleString(i18nLocale())} km</div>
                          <div className="text-xs text-[#6B726A]">{t("slots.distance_from")}</div>
                        </>
                      ) : <span className="text-xs text-[#6B726A]">—</span>}
                    </div>
                    <div className="text-xs space-y-1" data-testid={`recruit-acceptance-${p.candidate_id}`}>
                      {(accepted || confirmedByYou || declined || refused) && p.responded_at ? (
                        <div className="flex items-center gap-1.5 text-[#1f2421]">
                          <CalendarDays className="w-3.5 h-3.5 text-[#6B726A] shrink-0" /> {refused ? t("slots.refused_on", { date: fmtDateTime(p.responded_at) }) : t("slots.accepted_on", { date: fmtDateTime(p.responded_at) })}
                        </div>
                      ) : p.notified_at ? (
                        <div className="flex items-center gap-1.5 text-[#1f2421]">
                          <Mail className="w-3.5 h-3.5 text-[#6B726A] shrink-0" /> {t("slots.notified_on", { date: fmtDateTime(p.notified_at) })}
                        </div>
                      ) : null}
                      {declined || refused ? (
                        <div className="flex items-center gap-1.5 text-[#A53A2F] font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#A53A2F] inline-block shrink-0" />
                          {refused ? t("slots.refused_slot") : t("slots.declined_slot")}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-[#1A9E55] font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#1A9E55] inline-block shrink-0" />
                          {confirmedByYou ? t("slots.confirmed_chip") : accepted ? t("slots.accepted_slot") : t("slots.available_slot")}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 justify-end flex-wrap">
                      <button onClick={() => nav(`/replacements/${p.candidate_id}`)} data-testid={`recruit-view-profile-${p.candidate_id}`}
                        className="text-xs px-3.5 py-2 rounded-xl border border-[#1A4D2E]/30 text-[#1A4D2E] hover:bg-[#E8F0EA] inline-flex items-center gap-1.5 whitespace-nowrap">
                        <User className="w-3.5 h-3.5" /> {t("slots.view_profile")}
                      </button>
                      {accepted && (
                        <>
                          <button onClick={() => decide(p, true)} disabled={!!confirming} data-testid={`recruit-accept-${p.candidate_id}`}
                            className="text-xs px-3.5 py-2 rounded-xl bg-[#1A4D2E] text-white hover:bg-[#143C24] disabled:opacity-50 whitespace-nowrap inline-flex items-center gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            {confirming === `${p.id}-a` ? "…" : t("slots.accept_candidate")}
                          </button>
                          <button onClick={() => decide(p, false)} disabled={!!confirming} data-testid={`recruit-refuse-${p.candidate_id}`}
                            className="text-xs px-3.5 py-2 rounded-xl border border-[#E3655B]/40 text-[#A53A2F] hover:bg-[#FFF4F3] disabled:opacity-50 whitespace-nowrap">
                            {confirming === `${p.id}-r` ? "…" : t("slots.refuse_candidate")}
                          </button>
                        </>
                      )}
                      {confirmedByYou && (
                        <div className="flex flex-col items-end gap-1.5" data-testid={`recruit-decided-${p.candidate_id}`}>
                          <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-[#DCFCE7] text-[#166534] whitespace-nowrap inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> {t("slots.mission_assigned")}
                          </span>
                        </div>
                      )}
                      {declined && (
                        <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-[#FDE4E2] text-[#A53A2F] whitespace-nowrap"
                          data-testid={`recruit-decided-${p.candidate_id}`}>{t("slots.declined_chip")}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


export function SlotsTab({ need, bookings, proposals = [], stats, onChanged }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [howItWorks, setHowItWorks] = useState(false);
  const [rebroadcasting, setRebroadcasting] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [aiTips, setAiTips] = useState({});
  const [tipFor, setTipFor] = useState(null);

  const loadAiTip = (rowKey, cid) => {
    setTipFor(`${rowKey}-${cid}`);
    if (aiTips[cid]) return;
    setAiTips((m) => ({ ...m, [cid]: { loading: true } }));
    api.get(`/replacements/${cid}/ai-summary`)
      .then(({ data }) => setAiTips((m) => ({ ...m, [cid]: { opinion: data.ai_opinion || data.summary || null } })))
      .catch(() => setAiTips((m) => ({ ...m, [cid]: { opinion: null } })));
  };

  const rebroadcast = async () => {
    setRebroadcasting(true);
    try {
      const { data } = await api.post(`/needs/${need.id}/rebroadcast`);
      toast.success(t("slots.rebroadcast_done", { count: data?.notified ?? 0 }));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("slots.rebroadcast_error"));
    } finally {
      setRebroadcasting(false);
    }
  };

  const isPermanent = need.mode === "permanent";
  const keys = isPermanent ? (need.weekdays || []) : [...(need.dates || [])].sort();
  const slotsFor = (k) => (need.per_day && need.per_day[k]?.slots) || need.global_slots || [];

  const matchLabel = (v) =>
    v >= 85 ? t("slots.match_excellent") : v >= 75 ? t("slots.match_verygood") : v >= 60 ? t("slots.match_good") : t("slots.match_possible");

  const rows = useMemo(() => {
    const out = [];
    keys.forEach((k) => {
      slotsFor(k).forEach((s, i) => {
        out.push({ key: `${k}-${i}`, day: k, slot: s });
      });
    });
    return out;
  }, [need]); // eslint-disable-line react-hooks/exhaustive-deps

  const propsForDay = (day) =>
    isPermanent
      ? proposals.filter((p) => p.candidate_permanent_available)
      : proposals.filter((p) => (p.candidate_available_dates || []).includes(day));
  const totalPairs = rows.reduce((acc, r) => {
    const dp = propsForDay(r.day);
    return acc + (dp.some((p) => p.status === "confirmed") ? 1 : dp.length);
  }, 0);

  const decide = async (p, accept) => {
    setConfirming(`${p.id}-${accept ? "a" : "r"}`);
    try {
      await api.post(`/needs/${need.id}/proposals/${p.id}/decision`, { accept });
      toast.success(accept ? t("slots.decision_accept_done") : t("slots.decision_refuse_done"));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("slots.decision_error"));
    } finally {
      setConfirming(null);
    }
  };


  return (
    <div className="space-y-5" data-testid="need-tab-content-slots">
      {/* Bandeau matching automatique */}
      <div className="rounded-2xl border border-[#BFDBFE] bg-[#EFF6FF] p-4 flex items-start gap-3 flex-wrap" data-testid="auto-matching-banner">
        <div className="w-9 h-9 rounded-xl grid place-items-center shrink-0 bg-[#DBEAFE]">
          <Bot className="w-4 h-4 text-[#1E3A8A]" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-sm font-semibold text-[#1E3A8A]">{t("slots.banner_title")}</div>
          <div className="text-xs text-[#3B5B8C] mt-0.5">{t("slots.banner_desc")}</div>
          {stats?.last_run && (
            <div className="text-xs text-[#1E3A8A] mt-1.5 inline-flex items-center gap-1.5 bg-white/70 px-2.5 py-1 rounded-full" data-testid="matching-activity-line">
              <Clock className="w-3 h-3" />
              {t("slots.activity_summary", {
                analyzed: stats.last_run.analyzed ?? 0,
                notified: stats.last_run.notified ?? 0,
                unavailable: (stats.last_run.unavailable ?? 0) + (stats.last_run.engaged ?? 0),
              })}
              {" — "}{timeAgo(stats.last_run.ran_at)}
            </div>
          )}
          {howItWorks && (
            <div className="text-xs text-[#3B5B8C] mt-2 p-3 rounded-xl bg-white/70" data-testid="how-it-works-text">
              {t("slots.banner_detail")}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={rebroadcast} disabled={rebroadcasting} data-testid="rebroadcast-btn"
            className="text-xs px-3 py-2 rounded-full bg-[#1E3A8A] text-white hover:bg-[#16306F] disabled:opacity-50 whitespace-nowrap inline-flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${rebroadcasting ? "animate-spin" : ""}`} />
            {t("slots.rebroadcast")}
          </button>
          <button onClick={() => setHowItWorks((v) => !v)} data-testid="how-it-works-btn"
            className="text-xs px-3 py-2 rounded-full border border-[#BFDBFE] text-[#1E3A8A] bg-white hover:bg-[#DBEAFE] whitespace-nowrap">
            {t("slots.banner_how")}
          </button>
        </div>
      </div>

      {/* Candidats par plage horaire */}
      <div data-testid="slots-by-timeslot">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-base font-semibold" style={{ fontFamily: "Outfit" }}>{t("slots.by_slot_title")}</h3>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#E8F0EA] text-[#1A4D2E]" data-testid="slots-total-pairs">{totalPairs}</span>
        </div>
        <p className="text-xs text-[#6B726A]">{t("slots.by_slot_desc1")}</p>
        <p className="text-xs text-[#6B726A] mb-4">{t("slots.by_slot_desc2")}</p>

        <div className="space-y-3">
          {rows.length === 0 && (
            <div className="text-sm text-[#6B726A] p-4 rounded-xl bg-[#F7F8F6]">{t("slots.empty")}</div>
          )}
          {rows.map((r) => {
            const dayProps = propsForDay(r.day);
            const confirmedProp = dayProps.find((p) => p.status === "confirmed");
            const displayProps = confirmedProp ? [confirmedProp] : dayProps;
            const isOpen = !collapsed[r.key];
            return (
              <div key={r.key} className="rounded-2xl border border-[#BFDBFE]" data-testid={`slot-group-${r.key}`}>
                <button type="button" onClick={() => setCollapsed((c) => ({ ...c, [r.key]: !c[r.key] }))}
                  className={`w-full flex items-center gap-2.5 px-4 py-3 text-left rounded-t-2xl ${isOpen ? "" : "rounded-b-2xl"} bg-[#EFF6FF] hover:bg-[#DBEAFE]`}
                  data-testid={`slot-group-toggle-${r.key}`}>
                  {confirmedProp
                    ? <CheckCircle2 className="w-4 h-4 text-[#1E3A8A] shrink-0" />
                    : <Clock className="w-4 h-4 text-[#1E3A8A] shrink-0" />}
                  <span className="text-sm font-semibold capitalize text-[#1E3A8A]">
                    {isPermanent ? weekdayLabel(r.day) : `${fmtWeekday(r.day)} ${fmtDate(r.day)}`}
                  </span>
                  <span className="text-sm text-[#3B5B8C]">• {r.slot.start} - {r.slot.end}</span>
                  {confirmedProp ? (
                    <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534]" data-testid={`slot-badge-${r.key}`}>{t("slots.slot_filled")}</span>
                  ) : (
                    <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-[#DBEAFE] text-[#1E3A8A]" data-testid={`slot-badge-${r.key}`}>{t("slots.slot_ongoing")}</span>
                  )}
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white text-[#1E3A8A]">{displayProps.length}</span>
                  <ChevronDown className={`w-4 h-4 ml-auto shrink-0 transition-transform text-[#1E3A8A] ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (displayProps.length === 0 ? (
                  <div className="px-4 py-4 text-xs text-[#6B726A] border-t border-[#E2E5E0]">
                    {t("slots.no_candidates_slot")}
                  </div>
                ) : (
                  <div className="border-t border-[#E2E5E0]">
                    <div className="hidden md:grid px-4 py-2 gap-3 text-[10px] uppercase tracking-wider font-semibold text-[#6B726A] bg-[#FAFBF9] border-b border-[#E2E5E0] md:grid-cols-[minmax(180px,1.3fr)_minmax(110px,0.8fr)_minmax(90px,0.7fr)_minmax(180px,1.1fr)_minmax(140px,auto)]">
                      <div>{t("slots.col_candidate")}</div>
                      <div>{t("slots.col_compat")}</div>
                      <div>{t("slots.col_distance")}</div>
                      <div>{t("slots.col_acceptance")}</div>
                      <div className="text-right">{confirmedProp ? t("slots.col_status") : t("slots.col_actions")}</div>
                    </div>
                    <div className="divide-y divide-[#E2E5E0]">
                    {displayProps.map((p) => {
                      const avatarUrl = p.candidate_avatar
                        ? (p.candidate_avatar.startsWith("http") ? p.candidate_avatar : `${process.env.REACT_APP_BACKEND_URL}${p.candidate_avatar}`)
                        : null;
                      const score = typeof p.score === "number" && p.score > 0 ? p.score : null;
                      const accepted = p.status === "accepted";
                      const confirmedByYou = p.status === "confirmed";
                      const declined = p.status === "declined";
                      const refused = p.status === "refused";
                      return (
                        <div key={p.id}
                          className="px-4 py-3.5 grid gap-3 items-center md:grid-cols-[minmax(180px,1.3fr)_minmax(110px,0.8fr)_minmax(90px,0.7fr)_minmax(180px,1.1fr)_auto]"
                          data-testid={`slot-candidate-${r.key}-${p.candidate_id}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <Avatar name={p.candidate_name} url={avatarUrl} />
                            <div className="min-w-0">
                              <div className="text-sm font-semibold truncate">{p.candidate_name || t("needs_page.role_fallback")}</div>
                              <div className="text-xs text-[#6B726A] truncate">{p.candidate_role || t("needs_page.role_fallback")}</div>
                              {p.candidate_permanent_available && need?.mode === "permanent" && (
                                <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#E0F2F1] text-[#0E6E5D] border border-[#7FCBC4]"
                                  data-testid={`perm-badge-slot-${p.candidate_id}`}>
                                  <RefreshCcw className="w-2.5 h-2.5" /> Recrutement permanent
                                </span>
                              )}
                              {p.candidate_experience_years > 0 && (
                                <div className="text-[11px] text-[#6B726A] flex items-center gap-1 mt-0.5">
                                  <Users className="w-3 h-3" /> {t("slots.exp_years", { count: p.candidate_experience_years })}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="relative"
                            onMouseEnter={() => loadAiTip(r.key, p.candidate_id)}
                            onMouseLeave={() => setTipFor(null)}>
                            {score !== null ? (
                              <>
                                <span className="text-xs font-bold px-1.5 py-0.5 rounded cursor-help" style={{ background: "#E8F0EA", color: score >= 75 ? "#1A9E55" : "#C97A20" }}>{score}%</span>
                                <div className="h-1.5 w-24 bg-[#E2E5E0] rounded-full mt-1.5 overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${score}%`, background: score >= 75 ? "#1A9E55" : "#C97A20" }} />
                                </div>
                                <div className="text-[11px] text-[#4B5563] mt-1">{matchLabel(score)}</div>
                              </>
                            ) : <span className="text-xs text-[#6B726A]">—</span>}
                            {tipFor === `${r.key}-${p.candidate_id}` && (
                              <div className="absolute left-0 top-full mt-1.5 z-50 w-80 rounded-xl border border-[#D9CCFB] bg-white p-3.5 shadow-xl"
                                data-testid={`ai-tip-${r.key}-${p.candidate_id}`}>
                                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#3F2EB2] mb-1.5 flex items-center gap-1">
                                  <Sparkles className="w-3 h-3" /> Avis de l'IA
                                </div>
                                <p className="text-xs text-[#1f2421] leading-relaxed">
                                  {aiTips[p.candidate_id]?.loading
                                    ? "Analyse IA en cours…"
                                    : aiTips[p.candidate_id]?.opinion || "Analyse IA indisponible."}
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="text-sm">
                            {typeof p.candidate_distance_km === "number" ? (
                              <>
                                <div className="font-medium">{p.candidate_distance_km.toLocaleString(i18nLocale())} km</div>
                                <div className="text-xs text-[#6B726A]">{t("slots.distance_from")}</div>
                              </>
                            ) : <span className="text-xs text-[#6B726A]">—</span>}
                          </div>
                          <div className="text-xs space-y-1">
                            {(accepted || confirmedByYou || declined || refused) && p.responded_at ? (
                              <div className="flex items-center gap-1.5 text-[#1f2421]">
                                <CalendarDays className="w-3.5 h-3.5 text-[#6B726A] shrink-0" /> {refused ? t("slots.refused_on", { date: fmtDateTime(p.responded_at) }) : t("slots.accepted_on", { date: fmtDateTime(p.responded_at) })}
                              </div>
                            ) : p.notified_at ? (
                              <div className="flex items-center gap-1.5 text-[#1f2421]">
                                <Mail className="w-3.5 h-3.5 text-[#6B726A] shrink-0" /> {t("slots.notified_on", { date: fmtDateTime(p.notified_at) })}
                              </div>
                            ) : null}
                            {declined || refused ? (
                              <div className="flex items-center gap-1.5 text-[#A53A2F] font-medium">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#A53A2F] inline-block shrink-0" />
                                {refused ? t("slots.refused_slot") : t("slots.declined_slot")}
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5 text-[#1A9E55] font-medium">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#1A9E55] inline-block shrink-0" />
                                {confirmedByYou ? t("slots.confirmed_chip") : accepted ? t("slots.accepted_slot") : t("slots.available_slot")}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 justify-end flex-wrap">
                            <button onClick={() => nav(`/replacements/${p.candidate_id}`)} data-testid={`slot-view-profile-${r.key}-${p.candidate_id}`}
                              className="text-xs px-3.5 py-2 rounded-xl border border-[#1A4D2E]/30 text-[#1A4D2E] hover:bg-[#E8F0EA] inline-flex items-center gap-1.5 whitespace-nowrap">
                              <User className="w-3.5 h-3.5" /> {t("slots.view_profile")}
                            </button>
                            {accepted && (
                              <>
                                <button onClick={() => decide(p, true)} disabled={!!confirming} data-testid={`slot-accept-${r.key}-${p.candidate_id}`}
                                  className="text-xs px-3.5 py-2 rounded-xl bg-[#1A4D2E] text-white hover:bg-[#143C24] disabled:opacity-50 whitespace-nowrap inline-flex items-center gap-1.5">
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                  {confirming === `${p.id}-a` ? "…" : t("slots.accept_candidate")}
                                </button>
                                <button onClick={() => decide(p, false)} disabled={!!confirming} data-testid={`slot-refuse-${r.key}-${p.candidate_id}`}
                                  className="text-xs px-3.5 py-2 rounded-xl border border-[#E3655B]/40 text-[#A53A2F] hover:bg-[#FFF4F3] disabled:opacity-50 whitespace-nowrap">
                                  {confirming === `${p.id}-r` ? "…" : t("slots.refuse_candidate")}
                                </button>
                              </>
                            )}
                            {confirmedByYou && (
                              <div className="flex flex-col items-end gap-1.5" data-testid={`slot-decided-${r.key}-${p.candidate_id}`}>
                                <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-[#DCFCE7] text-[#166534] whitespace-nowrap inline-flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" /> {t("slots.mission_assigned")}
                                </span>
                                <span className="text-xs px-3 py-1.5 rounded-xl bg-[#F0F2EF] text-[#6B726A] whitespace-nowrap inline-flex items-center gap-1.5">
                                  <Lock className="w-3.5 h-3.5" /> {t("slots.slot_locked")}
                                </span>
                              </div>
                            )}
                            {declined && (
                              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-[#FDE4E2] text-[#A53A2F] whitespace-nowrap"
                                data-testid={`slot-decided-${r.key}-${p.candidate_id}`}>{t("slots.declined_chip")}</span>
                            )}
                            {refused && (
                              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-[#FDE4E2] text-[#A53A2F] whitespace-nowrap"
                                data-testid={`slot-refused-${r.key}-${p.candidate_id}`}>{t("slots.refused_chip")}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Comment ça fonctionne */}
      <div className="rounded-2xl border border-[#BFDBFE] bg-[#EFF6FF] p-4 flex items-start gap-3" data-testid="slots-how-footer">
        <div className="w-7 h-7 rounded-full bg-[#1E3A8A] grid place-items-center shrink-0 text-white text-xs font-bold">i</div>
        <div>
          <div className="text-sm font-semibold text-[#1E3A8A]">{t("slots.how_title")}</div>
          <div className="text-xs text-[#3B5B8C] mt-0.5">{t("slots.how_body")}</div>
        </div>
      </div>
    </div>
  );
}
